'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

// Event mappings from v1
const CONVERGE_EVENTS = [
  { key: 's_order_paid', label: 'Placed Order' },
  { key: 's_checkout_session_created', label: 'Started Checkout' },
  { key: 's_checkout_customer_email_valid', label: 'Added Payment Info' },
  { key: 's_subscription_canceled', label: 'Cancelled Subscription' },
  { key: 's_subscription_rebill_succeeded', label: 'Placed Recurring Subscription Order' },
] as const

const convergeFormSchema = z.object({
  webhookToken: z.string().min(1, 'Webhook Token is required'),
  endpoint: z.string().url('Please enter a valid URL'),
  allowedEvents: z.array(z.string()).nullish(),
})

type FormValues = z.infer<typeof convergeFormSchema>

interface ConvergeFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function ConvergeForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: ConvergeFormProps) {
  const { t } = useTranslation('integrations')

  // Show bullets if existing token exists
  const hasExistingToken =
    defaultValues.credentials?.webhookToken &&
    defaultValues.credentials.webhookToken.length > 0

  const form = useForm<FormValues>({
    resolver: zodResolver(convergeFormSchema),
    mode: 'all',
    defaultValues: {
      webhookToken: hasExistingToken ? '•'.repeat(32) : '',
      endpoint: defaultValues.credentials?.endpoint || 'https://app.runconverge.com/api/webhooks',
      allowedEvents: defaultValues.credentials?.allowedEvents || CONVERGE_EVENTS.map(e => e.key),
    },
  })

  const { register, handleSubmit, formState: { errors, touchedFields }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { webhookToken, endpoint, allowedEvents } = data
    await onSubmit({
      credentials: {
        allowedEvents: touchedFields.allowedEvents ? allowedEvents : defaultValues.credentials?.allowedEvents,
        webhookToken: touchedFields.webhookToken ? webhookToken : defaultValues.credentials?.webhookToken,
        endpoint: touchedFields.endpoint ? endpoint : (defaultValues.credentials?.endpoint || 'https://app.runconverge.com/api/webhooks'),
      },
    })
  }

  const allowedEvents = watch('allowedEvents') || []

  const toggleEvent = (eventKey: string, checked: boolean) => {
    const currentEvents = allowedEvents || []
    const newEvents = checked
      ? [...new Set([...currentEvents, eventKey])]
      : currentEvents.filter((e) => e !== eventKey)

    setValue('allowedEvents', newEvents, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Event Support Info */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('converge.eventSupport')}</AlertTitle>
        <AlertDescription>
          <div className="space-y-2">
            <p className="text-sm">{t('converge.eventsTracked')}</p>
            <ul className="list-inside list-disc space-y-1 text-sm">
              <li>{t('converge.eventPlacedOrder')}</li>
              <li>{t('converge.eventStartedCheckout')}</li>
              <li>{t('converge.eventAddedPaymentInfo')}</li>
              <li>{t('converge.eventCancelledSubscription')}</li>
              <li>{t('converge.eventPlacedRecurring')}</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      {/* Benefits */}
      <Alert variant="default" className="border-green-200 bg-green-50">
        <Zap className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-green-900">{t('converge.benefitsTitle')}</AlertTitle>
        <AlertDescription className="text-green-700">
          <ul className="list-inside list-disc space-y-1 text-sm">
            <li>{t('converge.benefit1')}</li>
            <li>{t('converge.benefit2')}</li>
            <li>{t('converge.benefit3')}</li>
            <li>{t('converge.benefit4')}</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* API Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('converge.apiConfiguration')}</CardTitle>
          <CardDescription>{t('converge.apiConfigDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhookToken">
              {t('converge.webhookToken')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="webhookToken"
              type="password"
              placeholder="wh_..."
              disabled={isLoading}
              {...register('webhookToken')}
            />
            {touchedFields.webhookToken && errors.webhookToken && (
              <p className="text-xs text-destructive">{errors.webhookToken.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="endpoint">
              {t('converge.webhookEndpoint')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="endpoint"
              type="url"
              placeholder="https://app.runconverge.com/api/webhooks"
              disabled={isLoading}
              {...register('endpoint')}
            />
            <p className="text-xs text-muted-foreground">
              {t('converge.webhookEndpointHint')}
            </p>
            {touchedFields.endpoint && errors.endpoint && (
              <p className="text-xs text-destructive">{errors.endpoint.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Event Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('converge.eventSelection')}</CardTitle>
          <CardDescription>{t('converge.eventSelectionDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {CONVERGE_EVENTS.map((event) => (
              <div key={event.key} className="flex items-center space-x-2">
                <Checkbox
                  id={event.key}
                  checked={allowedEvents.includes(event.key)}
                  onCheckedChange={(checked) => toggleEvent(event.key, checked as boolean)}
                  disabled={isLoading}
                />
                <Label htmlFor={event.key} className="cursor-pointer font-normal">
                  {event.label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('converge.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('converge.setupStep1')}{' '}
                <a
                  href="https://app.runconverge.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('converge.setupStep1Link')}
                </a>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('converge.setupStep2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('converge.setupStep3') }} />
              <li dangerouslySetInnerHTML={{ __html: t('converge.setupStep4') }} />
              <li>{t('converge.setupStep5')}</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
